import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency, formatDateBR, formatCPF, todayISO } from "@/lib/api";

function calcDuracao(saida, entrada, dias = 1) {
    if (!saida || !entrada) return "";
    const [h1, m1] = saida.split(":").map(Number);
    const [h2, m2] = entrada.split(":").map(Number);
    if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return "";
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins < 0) mins += 24 * 60;
    const extraDays = Math.max(0, (parseInt(dias) || 1) - 1);
    mins += extraDays * 24 * 60;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h >= 24) {
        const d = Math.floor(h / 24);
        const rh = h % 24;
        return `${d}d ${rh}h${String(m).padStart(2, "0")}min`;
    }
    return `${h}h${String(m).padStart(2, "0")}min`;
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Download, CalendarClock, Route, Printer, MessageCircle, Bell, Wrench } from "lucide-react";
import { tripWhatsAppText, tripReminderText, openWhatsApp } from "@/lib/whatsapp";
import { useAuth } from "@/contexts/AuthContext";

// Generic trips management for both fixo & diaria tabs
export default function TripForm({ tipo, title, subtitle }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const empty = { tipo, responsavel_nome: "", cpf: "", destino: "", destinos_extras: [], local_saida: "", veiculo_placa: "", veiculo_modelo: "", data: todayISO(), datas: tipo === "diaria" ? [todayISO()] : null, hora_saida: "", hora_entrada: "", dias: 1, km_saida: null, km_chegada: null, valor_diaria: "", valor: "", contrato_inicio: "", contrato_fim: "", telefone_motorista: "", chave_pix: "", observacoes: "" };
    const [trips, setTrips] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [filter, setFilter] = useState("");
    const [mes, setMes] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { tipo };
        if (mes) params.mes = mes;
        const [t, d, v] = await Promise.all([
            api.get("/trips", { params }),
            api.get("/drivers"),
            api.get("/vehicles", { params: { tipo } }),
        ]);
        setTrips(t.data);
        setDrivers(d.data);
        setVehicles(v.data);
        setLoading(false);
    }, [tipo, mes]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm({ ...empty, data: todayISO() }); setEditingId(null); setOpen(true); };
    const openEdit = (t) => {
        setForm({
            tipo: t.tipo, responsavel_nome: t.responsavel_nome, cpf: t.cpf,
            destino: t.destino, destinos_extras: t.destinos_extras || [], local_saida: t.local_saida || "",
            veiculo_placa: t.veiculo_placa, veiculo_modelo: t.veiculo_modelo || "",
            data: t.data, datas: t.tipo === "diaria" ? (t.datas && t.datas.length ? t.datas : [t.data]) : null,
            hora_saida: t.hora_saida || "", hora_entrada: t.hora_entrada || "",
            dias: t.dias || 1, km_saida: t.km_saida ?? null, km_chegada: t.km_chegada ?? null,
            valor_diaria: t.valor_diaria ?? "", valor: t.valor,
            contrato_inicio: t.contrato_inicio || "", contrato_fim: t.contrato_fim || "",
            telefone_motorista: t.telefone_motorista || "",
            chave_pix: t.chave_pix || "",
            observacoes: t.observacoes || "",
        });
        setEditingId(t.id);
        setOpen(true);
    };

    // Recalcula valor total quando muda valor_diaria ou dias
    const updateValorDiaria = (vd) => {
        const dias = parseInt(form.dias) || 1;
        const novoTotal = vd ? (parseFloat(vd) * dias) : form.valor;
        setForm({ ...form, valor_diaria: vd, valor: novoTotal ? Number(novoTotal.toFixed(2)) : "" });
    };

    const updateDias = (dias) => {
        const vd = parseFloat(form.valor_diaria);
        const diasNum = parseInt(dias) || 1;
        if (vd) {
            const novoTotal = Number((vd * diasNum).toFixed(2));
            setForm({ ...form, dias, valor: novoTotal });
        } else {
            setForm({ ...form, dias });
        }
    };

    // Diária: adicionar/remover/alterar datas avulsas. Nº de dias é derivado.
    const updateDatas = (novasDatas) => {
        const validas = novasDatas.filter(Boolean);
        const diasNum = Math.max(1, validas.length);
        const vd = parseFloat(form.valor_diaria);
        const novoValor = vd ? Number((vd * diasNum).toFixed(2)) : form.valor;
        setForm({ ...form, datas: novasDatas, dias: diasNum, data: validas[0] || form.data, valor: novoValor });
    };
    const addData = () => updateDatas([...(form.datas || []), ""]);
    const removeData = (idx) => updateDatas((form.datas || []).filter((_, i) => i !== idx));
    const changeData = (idx, val) => updateDatas((form.datas || []).map((d, i) => i === idx ? val : d));

    const selectDriver = (id) => {
        const d = drivers.find((x) => x.id === id);
        if (d) setForm({ ...form, responsavel_nome: d.nome, cpf: d.cpf });
    };

    const selectVehicle = (id) => {
        const v = vehicles.find((x) => x.id === id);
        if (!v) return;
        // Sugestão automática do valor mensal para contratos (fixo) conforme o tipo do veículo
        // Caminhonete: R$ 12.000,00 / mês • Ônibus: R$ 21.000,00 / mês
        const SUGGESTED_RATES = { caminhonete: 12000, onibus: 21000 };
        const sugerido = SUGGESTED_RATES[v.tipo_veiculo];
        const diasAtuais = parseInt(form.dias) || 1;
        if (
            tipo === "fixo" &&
            sugerido &&
            (!form.valor_diaria || parseFloat(form.valor_diaria) === 0)
        ) {
            setForm({
                ...form,
                veiculo_placa: v.placa,
                veiculo_modelo: v.modelo,
                valor_diaria: sugerido,
                valor: Number((sugerido * diasAtuais).toFixed(2)),
            });
            toast.success(`Valor mensal sugerido: ${sugerido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} (${v.tipo_veiculo})`);
            return;
        }
        setForm({ ...form, veiculo_placa: v.placa, veiculo_modelo: v.modelo });
    };

    const save = async () => {
        if (!form.responsavel_nome.trim() || !form.cpf.trim() || !form.destino.trim() || !form.veiculo_placa.trim() || !form.data) {
            toast.error("Preencha todos os campos obrigatórios");
            return;
        }
        // Para diária: garantir que datas válidas existam
        let datasFinal = null;
        let diasFinal = parseInt(form.dias) || 1;
        let dataPrincipal = form.data;
        if (tipo === "diaria") {
            const validas = (form.datas || []).filter(Boolean);
            if (validas.length === 0) {
                toast.error("Adicione pelo menos uma data");
                return;
            }
            datasFinal = validas;
            diasFinal = validas.length;
            dataPrincipal = validas[0];
        }
        const payload = {
            ...form,
            data: dataPrincipal,
            datas: datasFinal,
            destinos_extras: (form.destinos_extras || []).filter(Boolean),
            valor: parseFloat(form.valor) || 0,
            dias: diasFinal,
            valor_diaria: form.valor_diaria === "" || form.valor_diaria === null ? null : parseFloat(form.valor_diaria),
        };
        try {
            if (editingId) {
                await api.put(`/trips/${editingId}`, payload);
                toast.success("Viagem atualizada!");
            } else {
                await api.post("/trips", payload);
                toast.success("Viagem registrada!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/trips/${id}`);
            toast.success("Viagem removida");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const exportCsv = () => {
        const params = new URLSearchParams({ tipo });
        if (mes) params.set("mes", mes);
        window.open(`/api/trips/export?${params.toString()}`, "_blank");
    };

    const [fixRatesOpen, setFixRatesOpen] = useState(false);
    const [fixRatesLoading, setFixRatesLoading] = useState(false);
    const [fixRatesResult, setFixRatesResult] = useState(null);
    const runFixRates = async () => {
        setFixRatesLoading(true);
        try {
            const { data } = await api.post("/admin/tools/fix-contract-rates");
            setFixRatesResult(data);
            if (data.total_alterados > 0) {
                toast.success(`${data.total_alterados} contrato(s) corrigido(s)!`);
                load();
            } else {
                toast.info("Nenhum contrato precisou de correção.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao corrigir contratos");
        } finally {
            setFixRatesLoading(false);
        }
    };

    const filtered = trips.filter((t) =>
        (t.responsavel_nome + t.cpf + t.destino + t.veiculo_placa).toLowerCase().includes(filter.toLowerCase())
    );

    const totalFiltered = filtered.reduce((acc, t) => acc + (t.valor || 0), 0);
    const totalDias = filtered.reduce((acc, t) => acc + (t.dias || 1), 0);
    const Icon = tipo === "fixo" ? Route : CalendarClock;

    return (
        <div className="space-y-6" data-testid={`trips-${tipo}-page`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <Icon className="w-7 h-7 text-primary" /> {title}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                </div>
                <div className="flex gap-2">
                    {tipo === "fixo" && isAdmin && (
                        <Button
                            variant="outline"
                            onClick={() => { setFixRatesResult(null); setFixRatesOpen(true); }}
                            data-testid="btn-fix-contract-rates"
                            className="gap-2 border-amber-500 text-amber-700 hover:bg-amber-50"
                            title="Corrige o valor mensal dos contratos conforme o tipo do veículo (caminhonete R$ 12.000 / ônibus R$ 21.000)"
                        >
                            <Wrench className="w-4 h-4" /> Corrigir valores
                        </Button>
                    )}
                    <Button variant="outline" onClick={exportCsv} data-testid={`btn-export-${tipo}`} className="gap-2">
                        <Download className="w-4 h-4" /> Exportar CSV
                    </Button>
                    <Button onClick={openCreate} data-testid={`btn-new-trip-${tipo}`} className="gap-2">
                        <Plus className="w-4 h-4" /> Nova viagem
                    </Button>
                </div>
            </div>

            <Card>
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[220px] max-w-md">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Buscar..."
                                className="pl-10"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                data-testid={`input-search-${tipo}`}
                            />
                        </div>
                        <div>
                            <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-48" data-testid={`input-month-${tipo}`} />
                        </div>
                        {mes && (
                            <Button variant="ghost" size="sm" onClick={() => setMes("")}>Limpar mês</Button>
                        )}
                        <div className="ml-auto text-sm">
                            <span className="text-muted-foreground">Total: </span>
                            <strong className="text-primary text-lg">{formatCurrency(totalFiltered)}</strong>
                            <span className="text-muted-foreground ml-2">({filtered.length} viagens · {totalDias} dias)</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <Icon className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhuma viagem cadastrada</div>
                            <div className="text-sm text-muted-foreground mt-1">Clique em "Nova viagem" para registrar</div>
                        </div>
                    ) : (
                        <div className="w-full overflow-x-auto">
                            <table className="w-full text-sm table-auto">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-3 px-2 font-semibold whitespace-nowrap">Data</th>
                                        <th className="py-3 px-2 font-semibold">Responsável</th>
                                        <th className="py-3 px-2 font-semibold">Trajeto</th>
                                        <th className="py-3 px-2 font-semibold">Veículo</th>
                                        <th className="py-3 px-2 font-semibold text-center whitespace-nowrap">{tipo === "fixo" ? "Meses" : "Dias"}</th>
                                        <th className="py-3 px-2 font-semibold text-right whitespace-nowrap">Valor</th>
                                        <th className="py-3 px-2 font-semibold text-right whitespace-nowrap">Ações</th>
                                    </tr>
                                </thead>
                                <tbody data-testid={`trips-${tipo}-table-body`}>
                                    {filtered.map((t) => (
                                        <tr key={t.id} className="border-b border-border row-hover">
                                            <td className="py-3 px-2 whitespace-nowrap">
                                                {formatDateBR(t.data)}
                                                {t.datas && t.datas.length > 1 && (
                                                    <div className="text-xs text-muted-foreground">+{t.datas.length - 1} data{t.datas.length - 1 > 1 ? "s" : ""}</div>
                                                )}
                                            </td>
                                            <td className="py-3 px-2 font-medium">{t.responsavel_nome}</td>
                                            <td className="py-3 px-2 text-xs">
                                                {t.local_saida && <span className="text-muted-foreground">{t.local_saida} → </span>}
                                                <span className="font-medium">{t.destino}</span>
                                                {(t.hora_saida || t.hora_entrada) && (
                                                    <div className="text-muted-foreground mt-0.5">{t.hora_saida || "--:--"} → {t.hora_entrada || "--:--"}</div>
                                                )}
                                            </td>
                                            <td className="py-3 px-2 text-xs">
                                                {t.veiculo_modelo && <div>{t.veiculo_modelo}</div>}
                                                <span className="font-mono text-muted-foreground">{t.veiculo_placa}</span>
                                            </td>
                                            <td className="py-3 px-2 text-center font-medium">{t.dias || 1}</td>
                                            <td className="py-3 px-2 text-right font-semibold whitespace-nowrap">{formatCurrency(t.valor)}</td>
                                            <td className="py-3 px-2 text-right">
                                                <div className="flex gap-0.5 justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => openWhatsApp(tripWhatsAppText(t), t.telefone_motorista)} title="Enviar ficha por WhatsApp" data-testid={`btn-wa-trip-${t.id}`}>
                                                        <MessageCircle className="w-4 h-4 text-green-600" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openWhatsApp(tripReminderText(t), t.telefone_motorista)} title="Enviar lembrete por WhatsApp" data-testid={`btn-rem-trip-${t.id}`}>
                                                        <Bell className="w-4 h-4 text-yellow-600" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => window.open(`/viagem/${t.id}/imprimir`, "_blank")} title="Imprimir ficha" data-testid={`btn-print-trip-${t.id}`}>
                                                        <Printer className="w-4 h-4 text-primary" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`btn-edit-trip-${t.id}`}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(t)} data-testid={`btn-delete-trip-${t.id}`}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid={`trip-dialog-${tipo}`}>
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar viagem" : `Nova viagem ${tipo === "fixo" ? "(Fixo)" : "(Diária)"}`}</DialogTitle>
                        <DialogDescription>Registre os dados da viagem</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2 overflow-y-auto pr-2 flex-1">
                        <div>
                            <Label>Selecionar motorista cadastrado (opcional)</Label>
                            <Select onValueChange={selectDriver}>
                                <SelectTrigger data-testid="select-trip-driver"><SelectValue placeholder="Preencher automaticamente..." /></SelectTrigger>
                                <SelectContent>
                                    {drivers.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nenhum motorista cadastrado</div>}
                                    {drivers.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>{d.nome} — {formatCPF(d.cpf)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Responsável *</Label>
                                <Input value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} data-testid="input-trip-nome" />
                            </div>
                            <div>
                                <Label>CPF *</Label>
                                <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} data-testid="input-trip-cpf" />
                            </div>
                        </div>
                        <div>
                            <Label>Selecionar veículo cadastrado (opcional)</Label>
                            <Select onValueChange={selectVehicle}>
                                <SelectTrigger data-testid="select-trip-vehicle"><SelectValue placeholder="Preencher automaticamente..." /></SelectTrigger>
                                <SelectContent>
                                    {vehicles.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nenhum veículo deste tipo</div>}
                                    {vehicles.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>{v.modelo} — {v.placa}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Modelo do veículo</Label>
                                <Input value={form.veiculo_modelo} onChange={(e) => setForm({ ...form, veiculo_modelo: e.target.value })} data-testid="input-trip-modelo" />
                            </div>
                            <div>
                                <Label>Placa *</Label>
                                <Input value={form.veiculo_placa} onChange={(e) => setForm({ ...form, veiculo_placa: e.target.value.toUpperCase() })} data-testid="input-trip-placa" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Local de saída</Label>
                                <Input value={form.local_saida} onChange={(e) => setForm({ ...form, local_saida: e.target.value })} placeholder="Ex: Santarém - PA" data-testid="input-trip-local-saida" />
                            </div>
                            <div>
                                <Label>Destino principal *</Label>
                                <Input value={form.destino} onChange={(e) => setForm({ ...form, destino: e.target.value })} placeholder="Ex: Belterra - PA" data-testid="input-trip-destino" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Destinos adicionais <span className="text-xs text-muted-foreground font-normal">(opcional — para viagens com múltiplos pontos)</span></Label>
                                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, destinos_extras: [...(form.destinos_extras || []), ""] })} className="gap-1" data-testid="btn-add-destino">
                                    <Plus className="w-3 h-3" /> Adicionar destino
                                </Button>
                            </div>
                            {(form.destinos_extras || []).length > 0 && (
                                <div className="space-y-2">
                                    {(form.destinos_extras || []).map((d, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <span className="text-xs text-muted-foreground w-8 text-center font-mono">{idx + 2}°</span>
                                            <Input value={d} onChange={(e) => setForm({ ...form, destinos_extras: (form.destinos_extras || []).map((x, i) => i === idx ? e.target.value : x) })} placeholder="Próximo destino" data-testid={`input-destino-extra-${idx}`} />
                                            <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, destinos_extras: (form.destinos_extras || []).filter((_, i) => i !== idx) })} data-testid={`btn-remove-destino-${idx}`}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {tipo === "diaria" ? (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Datas da diária * <span className="text-xs text-muted-foreground font-normal">(Nº de dias = {(form.datas || []).filter(Boolean).length || 1})</span></Label>
                                    <Button type="button" variant="outline" size="sm" onClick={addData} className="gap-1" data-testid="btn-add-data">
                                        <Plus className="w-3 h-3" /> Adicionar data
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {(form.datas || []).map((d, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <span className="text-xs text-muted-foreground w-6 text-center">{idx + 1}.</span>
                                            <Input type="date" value={d} onChange={(e) => changeData(idx, e.target.value)} data-testid={`input-trip-data-${idx}`} />
                                            {(form.datas || []).length > 1 && (
                                                <Button type="button" variant="ghost" size="sm" onClick={() => removeData(idx)} data-testid={`btn-remove-data-${idx}`}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <Label>Valor por dia (R$)</Label>
                                    <Input type="number" step="0.01" value={form.valor_diaria} onChange={(e) => updateValorDiaria(e.target.value)} placeholder="Ex: 100.00" data-testid="input-trip-valor-diaria" />
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Data *</Label>
                                    <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="input-trip-data" />
                                </div>
                                <div>
                                    <Label>Valor por mês (R$)</Label>
                                    <Input type="number" step="0.01" value={form.valor_diaria} onChange={(e) => updateValorDiaria(e.target.value)} placeholder="Ex: 12000.00" data-testid="input-trip-valor-diaria" />
                                </div>
                            </div>
                        )}
                        <div className={tipo === "diaria" ? "grid grid-cols-2 gap-3" : "grid grid-cols-3 gap-3"}>
                            <div>
                                <Label>Horário de saída</Label>
                                <Input type="time" value={form.hora_saida} onChange={(e) => setForm({ ...form, hora_saida: e.target.value })} data-testid="input-trip-saida" />
                            </div>
                            <div>
                                <Label>Horário de chegada</Label>
                                <Input type="time" value={form.hora_entrada} onChange={(e) => setForm({ ...form, hora_entrada: e.target.value })} data-testid="input-trip-entrada" />
                            </div>
                            {tipo === "fixo" && (
                                <div>
                                    <Label>Nº de meses *</Label>
                                    <Input type="number" min="1" value={form.dias} onChange={(e) => updateDias(e.target.value)} placeholder="Ex: 2" data-testid="input-trip-dias" />
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>KM de saída</Label>
                                <Input type="number" step="0.1" value={form.km_saida ?? ""} onChange={(e) => setForm({ ...form, km_saida: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="Ex: 45230" data-testid="input-trip-km-saida" />
                            </div>
                            <div>
                                <Label>KM de chegada</Label>
                                <Input type="number" step="0.1" value={form.km_chegada ?? ""} onChange={(e) => setForm({ ...form, km_chegada: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="Ex: 45680" data-testid="input-trip-km-chegada" />
                            </div>
                        </div>
                        {(form.km_saida != null && form.km_chegada != null && form.km_chegada >= form.km_saida) && (
                            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 text-sm">
                                <span className="text-muted-foreground">KM rodados:</span>
                                <strong className="ml-2 text-primary">{(form.km_chegada - form.km_saida).toLocaleString("pt-BR")} km</strong>
                            </div>
                        )}
                        {(form.hora_saida && form.hora_entrada) && (
                            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 text-sm">
                                <span className="text-muted-foreground">Tempo de percurso calculado:</span>
                                <strong className="ml-2 text-primary">{calcDuracao(form.hora_saida, form.hora_entrada, form.dias)}</strong>
                            </div>
                        )}
                        <div>
                            <Label>Valor total (R$) *</Label>
                            <Input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} data-testid="input-trip-valor" />
                            {form.valor_diaria && form.dias && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    Cálculo: {parseFloat(form.valor_diaria).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} × {form.dias} {tipo === "fixo" ? (form.dias === 1 ? "mês" : "meses") : (form.dias === 1 ? "dia" : "dias")} = <strong className="text-primary">{(parseFloat(form.valor_diaria) * (parseInt(form.dias) || 1)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Telefone do motorista (WhatsApp)</Label>
                                <Input type="tel" value={form.telefone_motorista} onChange={(e) => setForm({ ...form, telefone_motorista: e.target.value })} placeholder="Ex: 93991234567" data-testid="input-trip-telefone" />
                            </div>
                            {tipo === "diaria" && (
                                <div>
                                    <Label>Chave PIX</Label>
                                    <Input type="text" value={form.chave_pix} onChange={(e) => setForm({ ...form, chave_pix: e.target.value })} placeholder="CPF, telefone, e-mail ou chave aleatória" data-testid="input-trip-pix" />
                                </div>
                            )}
                        </div>
                        {tipo === "fixo" && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Início do contrato</Label>
                                    <Input type="date" value={form.contrato_inicio} onChange={(e) => setForm({ ...form, contrato_inicio: e.target.value })} data-testid="input-trip-contrato-inicio" />
                                </div>
                                <div>
                                    <Label>Fim do contrato</Label>
                                    <Input type="date" value={form.contrato_fim} onChange={(e) => setForm({ ...form, contrato_fim: e.target.value })} data-testid="input-trip-contrato-fim" />
                                </div>
                            </div>
                        )}
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-trip-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        {editingId && (
                            <Button variant="secondary" className="gap-2" onClick={() => window.open(`/viagem/${editingId}/imprimir`, "_blank")} data-testid={`btn-print-dialog-${tipo}`}>
                                <Printer className="w-4 h-4" /> Imprimir
                            </Button>
                        )}
                        <Button onClick={save} data-testid={`btn-save-trip-${tipo}`}>Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover viagem?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Viagem de {confirmDelete?.responsavel_nome} para {confirmDelete?.destino} será removida.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid={`btn-confirm-delete-trip-${tipo}`}>Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Dialog: Corrigir valores dos contratos */}
            <Dialog open={fixRatesOpen} onOpenChange={setFixRatesOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-800">
                            <Wrench className="w-5 h-5" /> Corrigir valores dos contratos
                        </DialogTitle>
                        <DialogDescription>
                            Esta ferramenta ajusta o valor mensal de todos os contratos (fixo) conforme o tipo do veículo cadastrado:
                            <ul className="list-disc pl-5 mt-2 space-y-0.5">
                                <li><strong>Caminhonete</strong> → R$ 12.000,00 / mês</li>
                                <li><strong>Ônibus</strong> → R$ 21.000,00 / mês</li>
                            </ul>
                            O total do contrato (valor cheio) é recalculado automaticamente: <code>parcela × duração</code>.
                            Contratos sem veículo cadastrado ou com tipo diferente são mantidos inalterados.
                        </DialogDescription>
                    </DialogHeader>

                    {fixRatesResult ? (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="rounded border border-emerald-300 bg-emerald-50 p-2 text-center">
                                    <div className="text-xs uppercase text-emerald-800 font-bold">Alterados</div>
                                    <div className="text-2xl font-extrabold text-emerald-900">{fixRatesResult.total_alterados}</div>
                                </div>
                                <div className="rounded border border-gray-300 bg-gray-50 p-2 text-center">
                                    <div className="text-xs uppercase text-gray-700 font-bold">Já corretos</div>
                                    <div className="text-2xl font-extrabold text-gray-800">{fixRatesResult.inalterados}</div>
                                </div>
                                <div className="rounded border border-yellow-300 bg-yellow-50 p-2 text-center">
                                    <div className="text-xs uppercase text-yellow-800 font-bold">Sem veículo</div>
                                    <div className="text-2xl font-extrabold text-yellow-900">{fixRatesResult.sem_veiculo_vinculado}</div>
                                </div>
                            </div>
                            {fixRatesResult.alterados?.length > 0 && (
                                <div className="max-h-64 overflow-auto border rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-gray-100 sticky top-0">
                                            <tr>
                                                <th className="p-1 text-left">Responsável</th>
                                                <th className="p-1 text-left">Placa</th>
                                                <th className="p-1 text-left">Tipo</th>
                                                <th className="p-1 text-right">Diária antes</th>
                                                <th className="p-1 text-right">Diária depois</th>
                                                <th className="p-1 text-right">Total depois</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fixRatesResult.alterados.map((r) => (
                                                <tr key={r.id} className="border-b">
                                                    <td className="p-1">{r.responsavel_nome}</td>
                                                    <td className="p-1 font-mono">{r.veiculo_placa}</td>
                                                    <td className="p-1">{r.tipo_veiculo}</td>
                                                    <td className="p-1 text-right text-red-700 line-through">{formatCurrency(r.diaria_antes)}</td>
                                                    <td className="p-1 text-right text-emerald-800 font-bold">{formatCurrency(r.diaria_depois)}</td>
                                                    <td className="p-1 text-right font-bold">{formatCurrency(r.valor_depois)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            <strong>⚠️ Atenção:</strong> esta ação altera o <strong>valor mensal</strong> e o <strong>valor total</strong>
                            dos contratos existentes no banco. A operação é segura e idempotente (pode rodar várias vezes sem duplicar),
                            mas o dado antigo não é guardado. Confirme para prosseguir.
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFixRatesOpen(false)}>
                            {fixRatesResult ? "Fechar" : "Cancelar"}
                        </Button>
                        {!fixRatesResult && (
                            <Button
                                onClick={runFixRates}
                                disabled={fixRatesLoading}
                                data-testid="btn-confirm-fix-rates"
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                            >
                                {fixRatesLoading ? "Corrigindo..." : "Corrigir agora"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
